import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import Form from '../../common/Form';
import { closeModalAction } from '../../actions/modal';
import { getRolesList } from '../../actions/partners';
import { getSendInviteFlow, inviteUserRequest } from '../../actions/users';
import { motion, AnimatePresence } from 'framer-motion';
import { fieldTypes, GetInputElementFromObject } from '../../pages/Onboard/Fields';
import { SkeltonCardTwo } from '../../common/SkeltonCardTwo';
import { CLEAR_SEND_INVITE_FLOW } from '../../types/user';
import { actualValueForDropdown } from '../../utils/common';
import moment from 'moment';
import { transformAddress } from '../Inputs/Address';
import _ from 'lodash';

export const addNewVendorFromPoFormName = 'addNewVendorFromPo';

export default function AddNewVendor({ org_id }) {
    const methods = useForm({ mode: 'all' });
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const onboardingFlowLoading = useSelector((state) => state.loading.onboardingFlowLoading);
    const addVendorFromPoLoading = useSelector((state) => state.loading.addVendorFromPoLoading);
    const sendInviteFlow = useSelector((state) => state.user.sendInviteFlow);
    const inviteFormImagesUrls = useSelector((state) => state.user.inviteFormImagesUrls);

    useEffect(() => {
        return () => {
            dispatch({ type: CLEAR_SEND_INVITE_FLOW })
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(getRolesList({ org_id, source: 'invite', invite: true }))
    }, [dispatch, org_id]);

    const inviteRoles = useSelector((state) => state.partners.inviteRoles);

    const findVendorRole = useMemo(() => {
        if (inviteRoles && inviteRoles.length > 0) {
            return inviteRoles.find((role) => role.label === 'Vendor');
        }
        return null;
    }, [inviteRoles]);

    useEffect(() => {
        if (findVendorRole && findVendorRole.id) {
            dispatch(getSendInviteFlow({ role_ids: findVendorRole.id }));
        }
    }, [findVendorRole, dispatch]);

    const sendInviteFields = useMemo(() => {
        let fields = [];
        if (Array.isArray(sendInviteFlow) && sendInviteFlow.length > 0) {
            fields = sendInviteFlow;
        }
        return fields;
    }, [sendInviteFlow]);

    const groupedByDataType = useMemo(() => sendInviteFields.reduce((acc, item) => {
        const { ref } = item;
        acc[ref] = item.data_type
        return acc;
    }, {}), [sendInviteFields]);

    const submitFun = (formValues) => {
        const roles = [];
        inviteRoles.forEach(({ id }) => {
            if (formValues[id]) roles.push(id);
        });
        const fields = [];
        Object.entries(formValues).forEach(([key, value]) => {
            const getActualKey = (value) => {
                if (typeof value === 'string' && value.includes('::')) {
                    return value.split('::')[0];
                }
                return value?.value || value;
            };

            const actualKey = getActualKey(key);

            if (groupedByDataType[actualKey]) {
                let transformedValue;
                if (groupedByDataType[actualKey] === fieldTypes.CHECK_BOX) {
                    const findSimilar = Object.entries(formValues).filter(([k]) => actualKey === getActualKey(k)).filter(([k, v]) => v === true);
                    if (findSimilar.length > 1) {
                        transformedValue = [];
                        findSimilar.map(([k, v]) => {
                            if (v) {
                                transformedValue.push(k.split('::')[1]);
                            }
                        })
                    } else {
                        transformedValue = key.split('::')[1];
                    }
                } else if (groupedByDataType[actualKey] === fieldTypes.DATE) {
                    if (value) transformedValue = moment(value).format('YYYY-MM-DD H:m:s');
                } else if (groupedByDataType[actualKey] === fieldTypes.MULTI_MENU) {
                    if (value && value.length > 0) {
                        transformedValue = value.map(({ value }) => value);
                    }
                } else if (groupedByDataType[actualKey] === fieldTypes.IMG) {
                    const inviteFormImageUrl = inviteFormImagesUrls[actualKey];
                    transformedValue = inviteFormImageUrl;
                } else if (groupedByDataType[actualKey] === fieldTypes.ADDRESS) {
                    let tr = transformAddress(formValues, actualKey);
                    if (tr.place_id || tr.plain_text) transformedValue = tr;
                } else {
                    if (actualValueForDropdown(value)) transformedValue = actualValueForDropdown(value);
                }
                if (transformedValue) fields.push({ ref: actualKey, value: transformedValue, data_type: groupedByDataType[actualKey] });
            };
        });
        dispatch(inviteUserRequest({ org_id, role_ids: [findVendorRole.id], fields: _.uniqBy([...fields], 'ref'), resetOnSuccess: () => { }, formName: addNewVendorFromPoFormName, source: 'add_new_vendor_from_po' }));
    };


    return (
        <Form
            methods={methods}
            onSubmit={submitFun}
            showSubmit
            submitButtonText='Add'
            submitContainerClassName='flex justify-end gap-4 mt-8 mb-4 mr-4'
            onCancel={() => dispatch(closeModalAction())}
            isLoading={formButtonLoading[addNewVendorFromPoFormName]}
        >
            <div className='px-4 pt-4'>
                {onboardingFlowLoading || addVendorFromPoLoading ? (
                    <motion.div
                        className=''
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <SkeltonCardTwo arr={[1, 2, 3]} height={12} />
                    </motion.div>
                ) : sendInviteFields ? (
                    <motion.div
                        className="grid grid-cols-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                    >
                        <AnimatePresence>
                            {Array.isArray(sendInviteFields) && sendInviteFields.length > 0 && sendInviteFields.map((field, index) => {
                                const isInviteHidden = field?.hidden?.includes('INVITE');
                                const showRoles = field.label === 'Roles'

                                if (isInviteHidden || showRoles) {
                                    return null;
                                }

                                return (
                                    <GetInputElementFromObject fieldClass='' field={field} typeEnum={'INVITE'} />
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>
                ) : <div className='text-center text-sm text-gray-500'>No fields to show</div>}
            </div>
        </Form>
    );
}
